import React from "react";
import { Link } from "react-router-dom";
import { useEpSession } from "../EpSessionContext";
import { gradients, transition } from "../theme";

const headerGlassBtn = (disabled) => ({
  background: disabled ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.15)",
  color: disabled ? "rgba(255,255,255,0.4)" : "#fff",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: 10,
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: 700,
  transition,
  cursor: disabled ? "not-allowed" : "pointer",
});

const headerWarningBadge = (clickable) => ({
  fontSize: 12,
  color: "#fca5a5",
  background: "rgba(239,68,68,0.15)",
  border: "1px solid rgba(239,68,68,0.3)",
  borderRadius: 8,
  padding: "4px 10px",
  maxWidth: 280,
  textAlign: "right",
  lineHeight: 1.4,
  cursor: clickable ? "pointer" : "default",
});

const EpSessionHeader = ({
  iAmPresident,
  canClose,
  canCloseDefinitively,
  pendingDecisionCount,
  pendingDirectorCount,
  directorDecisions,
  isClosing,
  isEndingSession,
  onShowCalling,
  onFinalizeSession,
  onEndSession,
  onStartTour,
}) => {
  const { ep, isDirector, isEnded, inscriptions } = useEpSession();

  return (
    <div
      data-tour="header"
      className="d-flex justify-content-between align-items-center p-4 rounded mb-3"
      style={{ background: gradients.header, color: "#fff" }}
    >
      <div className="d-flex align-items-center" style={{ gap: 14 }}>
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.15)",
          }}
        >
          <i className="fa fa-users" style={{ fontSize: 22 }} />
        </div>
        <div>
          <h2
            className="mb-0 text-white"
            style={{ fontSize: 20, fontWeight: 700 }}
          >
            Session Équipe Pluridisciplinaire
          </h2>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            Vue Président de séance — Prise de décision
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <button
          className="btn"
          onClick={onStartTour}
          title="Guide d'utilisation"
          style={{
            background: "rgba(255,255,255,0.1)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 10,
            padding: "8px 12px",
            fontSize: 13,
            fontWeight: 600,
            transition,
          }}
        >
          <i className="fa fa-question-circle" style={{ fontSize: 14 }} />
        </button>
        {!ep.closedAt && (
          <button
            className="btn"
            onClick={onShowCalling}
            style={{
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              transition,
            }}
          >
            <i className="fa fa-arrow-left mr-2" style={{ fontSize: 11 }} />
            Revenir à l'appel
          </button>
        )}
        {!ep.closedAt && iAmPresident && (
          <div
            className="d-flex flex-column align-items-end"
            style={{ gap: 6 }}
          >
            {!canClose && inscriptions.length > 0 && (
              <div style={headerWarningBadge(false)}>
                <i className="fa fa-exclamation-circle mr-1" />
                {pendingDecisionCount} allocataire
                {pendingDecisionCount > 1 ? "s" : ""} sans décision
              </div>
            )}
            <button
              className="btn"
              onClick={onFinalizeSession}
              disabled={isClosing || !canClose}
              style={headerGlassBtn(isClosing || !canClose)}
            >
              {isClosing ? (
                <i className="fa fa-spinner fa-spin mr-2" />
              ) : (
                <i className="fa fa-lock mr-2" />
              )}
              Clôturer la session
            </button>
          </div>
        )}
        {ep.closedAt && !isEnded && isDirector && (
          <div
            className="d-flex flex-column align-items-end"
            style={{ gap: 6 }}
          >
            {!canCloseDefinitively && (
              <div
                onClick={() => {
                  const first = inscriptions.find(
                    (i) => !directorDecisions[i.id],
                  );
                  if (first) {
                    const el = document.getElementById(
                      `inscription-${first.id}`,
                    );
                    if (el)
                      el.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                  }
                }}
                style={headerWarningBadge(true)}
              >
                <i className="fa fa-exclamation-circle mr-1" />
                {pendingDirectorCount} allocataire
                {pendingDirectorCount > 1 ? "s" : ""} sans décision directeur
                <i className="fa fa-arrow-down ml-1" style={{ fontSize: 10 }} />
              </div>
            )}
            <button
              className="btn"
              onClick={onEndSession}
              disabled={isEndingSession || !canCloseDefinitively}
              style={{
                ...headerGlassBtn(isEndingSession || !canCloseDefinitively),
                background:
                  isEndingSession || !canCloseDefinitively
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(239,68,68,0.8)",
              }}
            >
              {isEndingSession ? (
                <i className="fa fa-spinner fa-spin mr-2" />
              ) : (
                <i className="fa fa-lock mr-2" />
              )}
              Finaliser l'EP
            </button>
          </div>
        )}
        {ep.closedAt && (!isDirector || isEnded) && (
          <div
            className="d-flex flex-column align-items-end"
            style={{ gap: 6 }}
          >
            <div
              className="d-flex align-items-center px-3 py-2 rounded"
              style={{
                background: isEnded
                  ? "rgba(239,68,68,0.2)"
                  : "rgba(34,197,94,0.2)",
                border: isEnded
                  ? "1px solid rgba(239,68,68,0.4)"
                  : "1px solid rgba(34,197,94,0.4)",
                gap: 8,
              }}
            >
              <i
                className={`fa fa-${isEnded ? "flag-checkered" : "check-circle"}`}
                style={{ fontSize: 16 }}
              />
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                {isEnded ? "Session finalisée" : "Session clôturée"}
              </span>
            </div>
            {isEnded && (
              <Link
                to={`/ep-v2/${ep.id}/pv`}
                className="btn btn-sm"
                style={{
                  background: "rgba(239,68,68,0.7)",
                  color: "#fff",
                  border: "1px solid rgba(239,68,68,0.4)",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <i className="fa fa-file-pdf-o mr-2" />
                Voir le procès-verbal
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EpSessionHeader;
